<script setup>
import { ref, computed, watch } from 'vue'
import _ from 'lodash';
import { useNotification } from "@kyvg/vue3-notification";
import moment from 'moment-timezone';
import PlayoutService from '@/services/PlayoutService';
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconClock from '@/components/icon/IconClock.vue'
import IconReplace from '@/components/icon/IconReplace.vue'
import AddPlayoutErrorAlert from '@/components/TVPlayout/AddPlayoutErrorAlert.vue'
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import Spinner from '@/components/ui/Spinner.vue'
import FloatingInput from '../ui/FloatingInput.vue'
import Alert from '../Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Sidebar from '../modals/Sidebar'
import ModalBox from './ModalBox.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { track, EVENTS } from '@/lib/tracking'

const store = useStore()
const { notify } = useNotification()

const props = defineProps({
	version: {
		type: String,
		default: 'v1',
		validator: (v) => ['v1', 'v2'].includes(v),
	},
})

const sidebar = ref()
const modalOpen = ref(false)
const error = ref(null)
const creating = ref(false)
const playoutModeDropdown = ref()

const isV2 = computed(() => props.version === 'v2')

const playout = ref({
	name: '',
	mode: null,
	region: null,
	timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
})

const playoutFps = ref(25)
const playoutResolution = ref('1280x720')

// Plan-gated options for new playouts (v2). FPS list is a comma-separated
// string like "25, 30"; resolution is the max allowed height (720 or 1080).
const allowedFpsList = computed(() => {
	const raw = store.getters['User/tvPlayoutAllowedFpsList']
	return String(raw ?? '')
		.split(',')
		.map((item) => Number(item.trim()))
		.filter((num) => !Number.isNaN(num) && num > 0)
})

const PLAYOUT_FPS_OPTIONS = [25, 29.97, 30, 50, 59.94, 60]

// NTSC rates are unlocked by their integer counterpart in the plan's FPS list.
const NTSC_FPS_BASE = { 29.97: 30, 59.94: 60 }

const isFpsAllowed = (fps) =>
	allowedFpsList.value.includes(fps) || allowedFpsList.value.includes(NTSC_FPS_BASE[fps])

const allowedResolution = computed(
	() => Number(store.getters['User/tvPlayoutAllowedResolution']) || 720,
)

const isResolutionAllowed = (height) => allowedResolution.value >= height

// Loop mode is plan-gated (Starter and similar packages don't include it).
const hasPlayoutLoopMode = computed(() => !!store.getters['User/hasPlayoutLoopMode'])
const loopModeUpgradeTooltip = 'Upgrade Plan — Loop mode isn’t available on the Starter plan.'

const selectPlayoutMode = (mode) => {
	if (mode === 'loop' && !hasPlayoutLoopMode.value) return
	playout.value.mode = mode
}

// Ultra / Events live plans — shorter upgrade CTA for FPS/resolution.
const ULTRA_OR_EVENTS_PLAN_IDS = [
	'698d84497375f3f790ec54b5',
	'698d845b7375f3f790ec54b6',
	'698d83927375f3f790ec54b1',
	'698d839e7375f3f790ec54b2',
]

const isUltraOrEventsPlan = computed(() =>
	ULTRA_OR_EVENTS_PLAN_IDS.includes(String(store.getters['User/livePlanPackageId'] || '')),
)

const applyAllowedDefaults = () => {
	playoutFps.value = isFpsAllowed(25) ? 25 : (allowedFpsList.value[0] ?? 25)
	playoutResolution.value = '1280x720'
}

const isDisabled = computed(() => {
	return !playout.value.name.trim() || !playout.value.mode
})
const emit = defineEmits(['playout-added'])

const toggleSidebar = () => {
	if (isV2.value) {
		modalOpen.value = !modalOpen.value
		return
	}
	sidebar.value.toggleSidebar()
}

const closeFlow = () => {
	if (isV2.value) {
		modalOpen.value = false
		return
	}
	sidebar.value?.closeSidebar()
}

const setPlayoutMode = ((mode) => {
	if (mode === 'loop' && !hasPlayoutLoopMode.value) return
	playout.value.mode = mode
	playoutModeDropdown.value.toggleDropdown()
})

const onReset = () => {
	playout.value = { name: '', mode: null, region: null, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
	applyAllowedDefaults()
	error.value = null
	creating.value = false
}

watch(modalOpen, (open) => {
	if (open) applyAllowedDefaults()
	else onReset()
})

const createPlayout = (async () => {
	if (creating.value || isDisabled.value) return
	creating.value = true
	error.value = null
	try {
		const newPayout = await PlayoutService.addPlayout(
			playout.value.name,
			playout.value.mode,
			playout.value.timeZone,
			isV2.value ? playoutFps.value : undefined,
			isV2.value ? playoutResolution.value : undefined,
		);
		track(EVENTS.TV_PLAYOUT_CREATED, {
			playoutId: newPayout?._id,
			mode: playout.value.mode,
			version: props.version,
		})
		emit('playout-added', newPayout)
		notify({ group: 'success', text: 'TV Playout created.' })
		closeFlow()
	} catch (err) {
		console.log(err);
		error.value = err.message
		creating.value = false
	}
})

defineExpose({ toggleSidebar })
</script>

<template>
	<ModalBox
		v-if="isV2"
		v-model="modalOpen"
		title="Create New Playout"
		size="sm"
		mobile-placement="bottom"
		header-class="!px-4 !py-3 lg:!px-6 lg:!py-5 h-12 flex items-center"
		footer-divider 
		@close="onReset"
	>
		<section class="px-4 py-3 lg:px-6 lg:py-5">
			<AddPlayoutErrorAlert :error="error" v-if="error" />

			<div class="mb-4">
				<FormInput label="Name" id="playout-name-v2" v-model="playout.name" placeholder="e.g. News Channel 1" />
			</div>

			<div class="mb-4">
				<label class="block text-tiny font-medium text-surface-text-1 mb-2">Type of Playout</label>
				<div class="grid grid-cols-2 gap-2">
					<button
						type="button"
						class="relative text-left rounded-lg p-3 !ring-1 transition-colors"
						:class="playout.mode === 'schedule' ? 'ring-main bg-main-transparent' : 'ring-surface-4 bg-surface-3 hover:ring-surface-5'"
						@click="selectPlayoutMode('schedule')"
					>
						<IconBase
							v-if="playout.mode === 'schedule'"
							width="14"
							height="14"
							class="absolute top-2 right-2 text-main"
						>
							<IconCheckmark />
						</IconBase>
						<div class="flex items-center gap-2">
							<IconBase
								width="15"
								height="15"
								class="shrink-0 text-surface-9"
							>
								<IconClock />
							</IconBase>
							<span class="text-sm font-medium text-white">Schedule</span>
						</div>
						<p class="text-tiny text-surface-text-1 mt-1 mb-0">Play content at set dates &amp; times.</p>
					</button>
					<Tooltip
						:active="!hasPlayoutLoopMode"
						:content="loopModeUpgradeTooltip"
						wrapper-class="block min-w-0"
						popper-class="max-w-[260px] text-sm"
					>
						<button
							type="button"
							class="relative w-full text-left rounded-lg p-3 !ring-1 transition-colors"
							:class="!hasPlayoutLoopMode
								? 'ring-surface-4 bg-surface-3 opacity-60 cursor-not-allowed'
								: playout.mode === 'loop'
									? 'ring-main bg-main-transparent'
									: 'ring-surface-4 bg-surface-3 hover:ring-surface-5'"
							:aria-disabled="!hasPlayoutLoopMode"
							@click="selectPlayoutMode('loop')"
						>
							<IconBase
								v-if="playout.mode === 'loop'"
								width="14"
								height="14"
								class="absolute top-2 right-2 text-main"
							>
								<IconCheckmark />
							</IconBase>
							<div class="flex items-center gap-2">
								<IconBase
									width="16"
									height="16"
									class="shrink-0 text-surface-9"
								>
									<IconReplace />
								</IconBase>
								<span class="text-sm font-medium text-white">Loop</span>
							</div>
							<p class="text-tiny text-surface-text-1 mt-1 mb-0">Play your playlist on repeat, 24/7.</p>
						</button>
					</Tooltip>
				</div>
			</div>

			<div class="grid grid-cols-1 gap-4 mb-4">
				<div>
					<label class="block text-tiny font-medium text-surface-text-1 mb-2">FPS</label>
					<div class="inline-flex rounded-md overflow-hidden bg-surface-3 w-full p-0.5">
						<button
							v-for="fps in PLAYOUT_FPS_OPTIONS"
							:key="fps"
							type="button"
							:disabled="!isFpsAllowed(fps)"
							class="flex-1 h-7 text-tiny font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md"
							:class="playoutFps === fps ? 'bg-main text-white' : 'text-surface-text-2 enabled:hover:text-white'"
							@click="playoutFps = fps"
						>
							{{ fps }}
						</button>
					</div>
				</div>
				<div>
					<label class="block text-tiny font-medium text-surface-text-1 mb-2">Resolution</label>
					<div class="inline-flex rounded-md overflow-hidden bg-surface-3 w-full p-0.5">
						<button
							type="button"
							:disabled="!isResolutionAllowed(720)"
							class="flex-1 h-7 text-tiny font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md"
							:class="playoutResolution === '1280x720' ? 'bg-main text-white' : 'text-surface-text-2 enabled:hover:text-white'"
							@click="playoutResolution = '1280x720'"
						>
							1280x720
						</button>
						<button
							type="button"
							:disabled="!isResolutionAllowed(1080)"
							class="flex-1 h-7 text-tiny font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md"
							:class="playoutResolution === '1920x1080' ? 'bg-main text-white' : 'text-surface-text-2 enabled:hover:text-white'"
							@click="playoutResolution = '1920x1080'"
						>
							1920x1080
						</button>
					</div>
				</div>
			</div>
			<Alert class="relative">
				<p v-if="isUltraOrEventsPlan" class="text-xs m-0">
					Higher frame rate and resolution require a higher plan.
					<router-link
						to="/subscribe"
						class="text-main hover:text-main-light underline font-medium"
						@click="closeFlow"
					>
						Upgrade
					</router-link>
					to unlock.
				</p>
				<p v-else class="text-xs m-0">
					FPS and resolution cannot be changed after creation. Please select carefully before creating your TV Playout. For higher FPS and resolution, you will need to
					<router-link
						to="/subscribe"
						class="text-main hover:text-main-light underline font-medium"
						@click="closeFlow"
					>
						upgrade your plan
					</router-link>.
				</p>
			</Alert>
		</section>
		<template #footer>
			<div class="px-4 py-3 lg:px-6 lg:py-5 h-14 flex items-center justify-end gap-2">
				<FormButton type="secondary" size="md" :disabled="creating" @click="closeFlow">Cancel</FormButton>
				<FormButton :disabled="isDisabled || creating" type="main" size="md" class="relative" @click="createPlayout">
					<span :class="creating ? 'invisible' : ''">Create Playout</span>
					<Spinner v-if="creating" classes="absolute inset-0" spinner-color="currentColor" spinner-size="14px" />
				</FormButton>
			</div>
		</template>
	</ModalBox>

	<Sidebar v-else ref="sidebar" :stage="1" @closed="onReset" width="md:w-[420px]">
		<template v-slot:stage-one>
			<div class="px-4 h-full">
				<h3 class="text-lg font-medium mb-2 mt-3">New TV Playout</h3>
				<p class="text-dark-7 mb-4 text-sm">Create TV Playout and schedule your content.</p>
				<AddPlayoutErrorAlert :error="error" v-if="error" />
				<div class="mb-3">
					<FloatingInput id="playout-name-v1" label="Playout name *" surface="2" dense v-model="playout.name" />
				</div>
				<div class="mb-3">
					<label class="text-dark-9 text-tiny">Type of Playout*</label>
					<drop-down-menu ref="playoutModeDropdown" type="secondary" customClasses="w-full text-sm h-8 bg-surface-3 hover:bg-surface-4 rounded-md flex items-center px-3 capitalize text-tiny">
						<template #toggle-button>
							{{ playout.mode ? playout.mode : 'Select an option' }}
							<icon-base class="ml-auto">
								<icon-chevron-down />
							</icon-base>
						</template>
						<template #menu-items>
							<drop-down-menu-item customClasses="max-w-full w-full h-auto flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny hover:bg-surface-4" @click="setPlayoutMode('schedule')">
								<h5 class="font-medium">Schedule</h5>
								<p class="text-surface-8">Schedule contents to be published at a specific date and time.</p>
								<p class="text-surface-8 italic">Previously known as Daily and Weekly mode.</p>
							</drop-down-menu-item>
							<hr>
							<drop-down-menu-item
								:customClasses="[
									'max-w-full w-full h-auto flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny',
									hasPlayoutLoopMode ? 'hover:bg-surface-4' : 'opacity-60 cursor-not-allowed',
								].join(' ')"
								@click="setPlayoutMode('loop')"
							>
								<h5 class="font-medium">Loop</h5>
								<p class="text-surface-8">Set up content to loop over and over again.</p>
								<p v-if="!hasPlayoutLoopMode" class="text-surface-7 italic mb-0">{{ loopModeUpgradeTooltip }}</p>
								<p v-else class="text-surface-8 italic">Previously known as Line Up mode.</p>
							</drop-down-menu-item>
						</template>
					</drop-down-menu>
				</div>
				<div class="text-center">
					<FormButton :disabled="isDisabled || creating" @click="createPlayout" type="primary" size="md"
						class="relative w-full mt-4 justify-center">
						<span :class="creating ? 'invisible' : ''">Create TV Playout</span>
						<Spinner v-if="creating" classes="absolute inset-0" spinner-color="currentColor" spinner-size="14px" />
					</FormButton>
					<FormButton type="secondary" size="md" class="w-full mb-3 mt-2 justify-center"
						@click="toggleSidebar">
						Cancel
					</FormButton>
				</div>
			</div>
		</template>
	</Sidebar>
</template>
